import * as React from "react";
import { Download, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge, Input } from "@/components/ui/primitives";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/overlays";
import { api } from "@/lib/api";
import { toast } from "sonner";

// ── Search ──────────────────────────────────────────────────────────────

export const SearchInput = ({
  value,
  onChange,
  placeholder = "Search…",
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) => {
  const [local, setLocal] = React.useState(value);

  // Debounce locally so typing feels instant but the API is not hit per keypress.
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (local !== value) onChange(local);
    }, 350);
    return () => clearTimeout(timer);
  }, [local]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => setLocal(value), [value]);

  return (
    <div className={cn("relative", className)}>
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <Input
        type="search"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="pl-8 pr-8"
      />
      {local && (
        <button
          type="button"
          onClick={() => {
            setLocal("");
            onChange("");
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Clear search"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

// ── Filter select ───────────────────────────────────────────────────────

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

export const FilterSelect = ({
  label,
  value,
  options,
  onChange,
  allLabel = "All",
  className,
}: {
  label: string;
  value: string | undefined;
  options: FilterOption[];
  onChange: (value: string | undefined) => void;
  allLabel?: string;
  className?: string;
}) => (
  <Select
    value={value ?? "__all__"}
    onValueChange={(v) => onChange(v === "__all__" ? undefined : v)}
  >
    <SelectTrigger className={cn("w-auto min-w-[9rem] gap-2", className)} aria-label={label}>
      <span className="text-muted-foreground">{label}:</span>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="__all__">{allLabel}</SelectItem>
      {options.map((opt) => (
        <SelectItem key={opt.value} value={opt.value}>
          {opt.label}
          {opt.count != null && (
            <span className="ml-1.5 text-muted-foreground">({opt.count})</span>
          )}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

// ── Toolbar shell ───────────────────────────────────────────────────────

export const Toolbar = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn("flex flex-wrap items-center gap-2", className)}>{children}</div>
);

export const ActiveFilters = ({
  filters,
  onClear,
  onClearAll,
}: {
  filters: { key: string; label: string }[];
  onClear: (key: string) => void;
  onClearAll?: () => void;
}) => {
  if (filters.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {filters.map((f) => (
        <Badge key={f.key} variant="secondary" className="gap-1 pr-1">
          {f.label}
          <button
            type="button"
            onClick={() => onClear(f.key)}
            className="rounded p-0.5 hover:bg-foreground/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            aria-label={`Remove filter ${f.label}`}
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      {filters.length > 1 && onClearAll && (
        <Button variant="ghost" size="sm" onClick={onClearAll} className="h-6 px-2 text-xs">
          Clear all
        </Button>
      )}
    </div>
  );
};

// ── Bulk action bar ─────────────────────────────────────────────────────

export const BulkActionBar = ({
  count,
  onClear,
  children,
}: {
  count: number;
  onClear: () => void;
  children: React.ReactNode;
}) => {
  if (count === 0) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-lg border border-primary/30 bg-primary/[0.07] px-4 py-2.5"
      role="region"
      aria-label="Bulk actions"
    >
      <p className="text-sm font-medium" aria-live="polite">
        {count} selected
      </p>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      <Button variant="ghost" size="sm" onClick={onClear} className="ml-auto">
        Clear
      </Button>
    </div>
  );
};

// ── Export ──────────────────────────────────────────────────────────────

export const ExportButton = ({
  path,
  query,
  label = "Export CSV",
}: {
  path: string;
  query?: Record<string, unknown>;
  label?: string;
}) => {
  const [busy, setBusy] = React.useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await api.download(path, query);
      toast.success("Export downloaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button variant="outline" size="sm" onClick={run} loading={busy}>
      <Download aria-hidden />
      {label}
    </Button>
  );
};

// ── Page header ─────────────────────────────────────────────────────────

export const PageHeader = ({
  title,
  description,
  actions,
  className,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "flex flex-wrap items-start justify-between gap-3 border-b border-border pb-4",
      className,
    )}
  >
    <div className="space-y-1">
      <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
      {description && (
        <p className="max-w-2xl text-sm text-muted-foreground">{description}</p>
      )}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);
